import React, { useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import icons from "../../../assets/images/constantIcons";
import apiService, { handleError } from "../../Common/ApiCommon/ApiService";
import toast from "react-hot-toast";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { TimePicker } from 'antd';
import dayjs from "dayjs";
import { UserContext } from "../../Layout/UserContext";

const PayNow = ({ isOpen, onClose, booking_id, fetchPlayers, startTime, tableCharge, tableName, loading }) => {
  const [PlayersData, setPlayersData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const [showPlayerPopup, setShowPlayerPopup] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showResetPopup, setShowResetPopup] = useState(false);
  const [tableId, setTableId] = useState()
  const { triggerNotificationRefresh } = useContext(UserContext);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);

  // get current time in default
  const getCurrentTime = () => {
    const now = new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;

    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    let hours = now.getHours();
    const mins = now.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${String(hours).padStart(2, "0")}:${mins} ${ampm}`;
  };

  useEffect(() => {
    if (isOpen) {
      const updatedTime = getCurrentTime();
      formik.setFieldValue("end_time", updatedTime);
    }
  }, [isOpen])

  const formik = useFormik({
    initialValues: {
      previous_amount: "",
      table_charge: tableCharge,
      start_time: startTime,
      today_amount: "",
      discount: "",
      amount_to_be_pay: "",
      payable_ids: [],
      duration: "",
      end_time: getCurrentTime(),
      payment_type: "",
      end_date: new Date().toISOString().split("T")[0],
      balanceDue: ""
    },
    validationSchema: Yup.object({
      payable_ids: Yup.array()
        .of(Yup.number().required("Payable ID must be a number"))
        .min(1, "Payable is required"),
      discount: Yup.number().min(0, "Cannot be negative"),
      amountBeingPaid: Yup.number()
        .min(0, "Cannot be negative")
        .required("Amount is required"),
      duration: Yup.string().required("Duration is required"),
      end_time: Yup.string()
        .required("End time is required"),
      payment_type: Yup.string().when("amount_to_be_pay", (amount, schema) => {
        if (amount >= 0) {
          return schema.required("Payment mode is required");
        }
        return schema;
      }),
      end_date: Yup.string()
        .required("End date is required")
        .test(
          "is-greater",
          "End date must be greater than Start date",
          function (value) {
            const { payable_id } = this.parent;
            const selectedPlayer = PlayersData?.players?.find(
              (player) => player.id === payable_id
            );
            if (!selectedPlayer || !selectedPlayer.start_date) return true;
            const startDate = new Date(selectedPlayer.start_date.split("T")[0]);
            const endDate = new Date(value);
            return endDate > startDate;
          }
        ),
    }),
    onSubmit: async (values) => {
      console.log("values", values);

      setIsSubmitting(true);
      // Find the selected player data
      const selectedPlayer = PlayersData?.players?.find(
        (player) => player.id === values.payable_ids[0]
      );
      if (selectedPlayer) {
        let { start_date, start_time } = selectedPlayer;
        let { end_date, end_time } = values;
        start_date = start_date.split("T")[0];

        // Convert both times to 24-hour format for comparison
        const convertedStartTime = start_time.includes("AM") || start_time.includes("PM")
          ? apiService.convert12To24Hr(start_time)
          : start_time;

        const convertedEndTime = end_time.includes("AM") || end_time.includes("PM")
          ? apiService.convert12To24Hr(end_time)
          : end_time;

        if (start_date && convertedStartTime && end_date && convertedEndTime) {
          const startDateTime = new Date(`${start_date}T${convertedStartTime}`);
          const endDateTime = new Date(`${end_date}T${convertedEndTime}`);

          if (isNaN(startDateTime) || isNaN(endDateTime)) {
            toast.error("Invalid start or end date/time.");
            setIsSubmitting(false);
            return;
          }

          if (endDateTime <= startDateTime) {
            toast.error("End time must be greater than start time.");
            setIsSubmitting(false);
            return;
          }
        }
      }
      const payload = {
        booking: {
          booking_id,
          payable_ids: values.payable_ids,
          end_time: values.end_time,
          duration: values.duration,
          previous_amount: Number(values.previous_amount),
          today_amount: Number(values.today_amount),
          discount: Number(values.discount),
          amount_to_be_pay: Number(values.amountBeingPaid),
          payment_type: values.payment_type,
          end_date: values.end_date,
        },
      };
      try {
        const response = await apiService.post("/bookings/proceed_to_payment", payload);
        setTableId(response.data.table_id)
        if (response.status === 200) {

          triggerNotificationRefresh();
          if (response.status === 200) {
            toast.promise(
              new Promise((resolve) => setTimeout(resolve, 300)),
              {
                loading: "Processing...",
                success: response.message,
                error: "Something went wrong",
              },
              { duration: 3000 }
            );
            setShowResetPopup(true);
          }

        }
      } catch (errors) {
        handleError(errors);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Recalculate Payable Amount Automatically
  useEffect(() => {
    const previousAmount = Number(formik.values.previous_amount) || 0;
    const todayAmount = Number(formik.values.today_amount) || 0;
    const discountAmount = parseFloat(formik.values.discount) || 0;
    let totalAmount = previousAmount + todayAmount - discountAmount;
    let payableAmount = totalAmount < 0 ? 0 : totalAmount;


    formik.setFieldValue("amount_to_be_pay", totalAmount.toFixed(2));
    formik.setFieldValue("amountBeingPaid", payableAmount.toFixed(2));
  }, [
    formik.values.discount, formik.values.previous_amount, formik.values.today_amount, formik.values.selected_player,]);

  // Recalculate Balance Due whenever amountBeingPaid or amount_to_be_pay changes
  useEffect(() => {
    const totalAmount = Number(formik.values.amount_to_be_pay) || 0;
    const amountPaid = Number(formik.values.amountBeingPaid) || 0;

    const balanceDue = totalAmount - amountPaid;
    formik.setFieldValue("balanceDue", balanceDue.toFixed(2));
  }, [formik.values.amountBeingPaid, formik.values.amount_to_be_pay]);

  // fetch the players
  const fetchPlayer = async () => {
    try {
      const response = await apiService.get(`/bookings/pay_for_booking`, { booking: { booking_id }, });
      const tableCharge = response.data.table_charge || 0;
      const start_date = response.data.start_date;
      const start_time = response.data.start_time;
      // passing  each player booking  table_charge
      const playersWithTableCharge = response.data.players.map((player) => ({
        ...player,
        table_charge: tableCharge,
        start_date: start_date,
        start_time: start_time,
      }));
      // Store updated players data
      setPlayersData({
        ...response.data,
        players: playersWithTableCharge,
      });

      if (playersWithTableCharge.length === 1) {
        const onlyPlayer = playersWithTableCharge[0];
        formik.setFieldValue("payable_ids", [onlyPlayer.id]);
        formik.setFieldValue("previous_amount", onlyPlayer.amount);
      }
    } catch (errors) {
      handleError(errors);
    }
  };
  useEffect(() => {
    fetchPlayer();
  }, []);

  const fetchEndTimeData = async (end_time, end_date) => {
    const formattedEndTime = apiService.convert24To12Hr(end_time);
    try {
      const response = await apiService.get(
        `/bookings/calculate_end_time?booking[booking_id]=${booking_id}&booking[end_time]=${formattedEndTime}&booking[end_date]=${end_date}`
      );
      if (response?.duration && response?.today_amount !== undefined) {
        formik.setFieldValue("duration", response.duration);
        formik.setFieldValue("today_amount", response.today_amount);
        fetchPlayer();
      }
    } catch (errors) {
      handleError(errors);
    }
  };

  useEffect(() => {
    if (formik.values.end_time && formik.values.end_date) {
      const [hours, minutes] = formik.values.end_time.split(":");
      if (hours && minutes) {
        fetchEndTimeData(formik.values.end_time, formik.values.end_date);
      }
    }
  }, [formik.values.end_time, formik.values.end_date]);

  const playerOptions =
    PlayersData?.players?.map((player) => ({
      label: player.name,
      value: player.id,
    })) || [];

  // restart the booking
  const restartBooking = async () => {
    try {
      const allPlayerIds = PlayersData?.players?.map(player => player.id) || [];
      const payload = {
        booking: {
          table_id: Number(tableId),
          start_time: formik.values.end_time,
          date: formik.values.end_date,
          players: allPlayerIds,
        },
      };

      const response = await apiService.post("/bookings", payload);
      if (response.status === 200) {
        onClose();
        toast.success(response?.message, { duration: 2000 });
        formik.resetForm();
        fetchPlayers();
      }
    } catch (errors) {
      handleError(errors);
    }
  }
  if (!isOpen) return null;
  if (loading) return <p>Loading ...</p>

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg p-4 s:p-6 w-[310px] s:w-[350px] md:w-[460px]  relative">
        <div
          className="absolute top-7 right-3 bg-green50 rounded-full p-2 cursor-pointer w-[32px] h-[32px] flex justify-center items-center"
          onClick={() => { formik.resetForm(); onClose() }}
        ><icons.IoClose className=" text-green700 text-xl" />
        </div>
        <h2 className="text-xl font-semibold mb-5 text-G500 text-start">   Pay Now  </h2>
        {/* Form */}
        <form onSubmit={formik.handleSubmit} className="space-y-4 text-start h-[70vh] overflow-y-auto vertical">
          {/* payable  */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2">Payable <span className="text-red ">*</span></label>
            <Select
              options={playerOptions}
              name="payable_ids"
              value={playerOptions.find(option =>
                formik.values?.payable_ids?.[0] === option.value
              )}
              onChange={(selectedOption) => {
                const id = Number(selectedOption?.value);
                formik.setFieldValue("payable_ids", [id]);

                const selectedPlayer = PlayersData?.players?.find(player => player.id === id);

                if (selectedPlayer) {
                  formik.setFieldValue("previous_amount", selectedPlayer.amount);
                  setSelectedTable({
                    start_date: selectedPlayer.start_date,
                    start_time: selectedPlayer.start_time,
                  });
                } else {
                  formik.setFieldValue("previous_amount", 0);
                  setSelectedTable(null);
                }
              }}
            />
            {formik.touched.payable_ids && formik.errors.payable_ids && (
              <div className="text-red text-sm">{formik.errors.payable_ids}</div>
            )}
            <button
              className="text-sm text-green hover:text-blue hover:underline mt-1"
              onMouseEnter={() => setShowPlayerPopup(true)}
              onMouseLeave={() => setShowPlayerPopup(false)}
            >
              view booking details
            </button>
          </div>

          {showPlayerPopup && (
            <div className="absolute bg-gray-50 p-2 shadow-md border rounded-md w-auto z-30">
              {/* Table Info Section */}
              <div className="mb-3">
                <p className="text-sm"><strong>Table Name:</strong> <span className="pl-2">{tableName}</span></p>
                <p className="text-sm"><strong>Table Charge:</strong> <span className="pl-2">₹{tableCharge}</span></p>
                <p className="text-sm"><strong>Start Time:</strong> <span className="pl-2">{startTime}</span></p>
              </div>
              {/* Player Info Table */}
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="border px-3 py-1">Player</th>
                    <th className="border px-3 py-1">Previous Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {PlayersData?.players
                    ?.filter((player) => formik.values.payable_ids.includes(player.id))
                    .map((player) => (
                      <tr key={player.id}>
                        <td className="border px-3 py-1">{player.name}</td>
                        <td className="border px-3 py-1">₹{Math.trunc(player.amount)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* end date */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2">End Date <span className="text-red ">*</span></label>
            <div className="relative">
              <DatePicker
                selected={
                  formik.values.end_date
                    ? new Date(formik.values.end_date)
                    : null
                }
                onChange={(end_date) =>
                  formik.setFieldValue(
                    "end_date",
                    end_date.toISOString().split("T")[0]
                  )
                }
                minDate={(() => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  yesterday.setHours(0, 0, 0, 0);
                  return yesterday;
                })()}
                maxDate={(() => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return today;
                })()}
                placeholderText="yyyy-mm-dd"
                dateFormat="yyyy-MM-dd"
                className="inputtext placeholder:text-G50 w-full pr-10"
              />
              <icons.MdOutlineCalendarMonth className="absolute right-3 top-3 text-green pointer-events-none" />
            </div>
            {formik.touched.end_date && formik.errors.end_date && (
              <div className="text-red text-sm">{formik.errors.end_date}</div>
            )}
          </div>

          {/* End Time */}
          <div className="text-G300 flex  items-start flex-col">
            <label className="block text-sm font-normal mb-2">
              End Time <span className="text-red ">*</span>
            </label>
            <div className="w-full">
              <TimePicker
                value={formik.values.end_time ? dayjs(formik.values.end_time, "hh:mm A") : null}
                format="hh:mm A"
                use12Hours
                minuteStep={5}
                allowClear={false}
                inputReadOnly={true}
                showNow={true}
                renderExtraFooter={() => null}
                className="inputtext w-full focus:outline-none outline-none"
                onSelect={(time) => {
                  if (time) {
                    formik.setFieldValue("end_time", time.format("hh:mm A"));
                  }
                }}
                popupClassName="custom-timepicker-popupok"
                getPopupContainer={() => document.body}
              />
            </div>

            {formik.touched.end_time && formik.errors.end_time && (
              <div className="text-red text-sm">
                {formik.errors.end_time}
              </div>
            )}
          </div>

          {/* Duration */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Duration <span className="text-red ">*</span>
            </label>
            <input
              type="text"
              name="duration"
              className="inputtext"
              placeholder="HH:MM"
              value={formik.values.duration}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled
            />
            {formik.touched.duration && formik.errors.duration && (
              <div className="text-red text-sm text-start">{formik.errors.duration}  </div>
            )}
          </div>
          {/* Amount */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Today Amount <span className="text-red ">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
              <input
                type="number"
                name="today_amount"
                className="inputtext text-green placeholder:text-green"
                style={{ paddingLeft: "25px" }}
                placeholder="0"
                value={Math.trunc(formik.values.today_amount)}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled
              />
            </div>
            {formik.touched.today_amount && formik.errors.today_amount && (
              <div className="text-red text-sm text-start">  {formik.errors.today_amount}  </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4">
            <label className="text-sm font-normal text-G500">Want to Add a Discount ?</label>
            <button
              type="button"
              className={`px-3 py-1 rounded-full text-sm border ${showDiscount ? 'bg-green text-white' : 'bg-white text-green border-green'}`}
              onClick={() => setShowDiscount(!showDiscount)}
            >
              {showDiscount ? 'Remove' : 'Add'}
            </button>
          </div>

          {/* Discount Amount */}
          {showDiscount && (
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2 text-start">
                Discount Amount (if any)
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="number"
                  name="discount"
                  className="inputtext text-green placeholder:text-green"
                  placeholder="0"
                  style={{ paddingLeft: "25px" }}
                  value={formik.values.discount}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Prevent negative input
                    if (value.includes("-")) {
                      value = "0"; // Reset to 0 if user tries to enter "-"
                    }
                    let numericValue = value === "" ? "" : parseFloat(value) || 0;
                    const totalAmount =
                      (parseFloat(formik.values.previous_amount) || 0) +
                      (parseFloat(formik.values.today_amount) || 0);

                    if (numericValue > totalAmount) {
                      if (!isToastShown) {
                        setIsToastShown(true);
                        toast.error(
                          "Discount cannot be greater than the total amount",
                          { duration: 2000, onClose: () => setIsToastShown(false), });
                      }
                    } else {
                      setIsToastShown(false);
                      formik.setFieldValue("discount", numericValue);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-") { e.preventDefault() }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      formik.setFieldValue("discount", 0);
                    }
                  }}
                  onWheel={(e) => e.target.blur()} // Prevent number scrolling by removing focus
                />
              </div>
              {formik.touched.discount && formik.errors.discount && (
                <div className="text-red text-sm text-start">{formik.errors.discount} </div>
              )}
            </div>
          )}
          {/* Total amount */}
          <div className="text-G300">
            <label className="block text-sm font-normal mb-2 text-start">
              Total Amount (Prev + Today) <span className="text-red ">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
              <input
                type="number"
                name="amount_to_be_pay"
                className="inputtext text-green"
                placeholder="0"
                style={{ paddingLeft: "25px" }}
                value={Math.trunc(formik.values.amount_to_be_pay)}
                disabled
              />
            </div>
          </div>

          {/* Payment mode */}
          {formik.values.amount_to_be_pay >= 0 && (
            <div className="flex flex-col text-G300 ">
              <label className="block font-normal text-sm mb-2">
                Payment mode <span className="text-red ">*</span>
              </label>
              <select
                name="payment_type"
                className="inputtext "
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.payment_type}
              >
                <option value="">Select payment mode</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
              </select>
              {formik.touched.payment_type && formik.errors.payment_type && (
                <div className="text-red text-sm"> {formik.errors.payment_type} </div>
              )}
            </div>
          )}

          <div className="flex gap-4">
            {/* Amount to be Paid */}
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2 text-start">
                Amount to be Paid <span className="text-red ">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="text"
                  name="amountBeingPaid"
                  className="inputtext text-green placeholder:text-green"
                  placeholder="0"
                  style={{ paddingLeft: "25px" }}
                  value={Math.trunc(formik.values.amountBeingPaid) === "" ? "" : Math.trunc(formik.values.amountBeingPaid)}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Prevent non-numeric input
                    if (!/^\d*$/.test(value)) return;
                    formik.setFieldValue("amountBeingPaid", value === "" ? "" : Number(value));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") {
                      e.preventDefault();
                    }
                  }}
                />
              </div>
              {formik.touched.amountBeingPaid && formik.errors.amountBeingPaid && (
                <div className="text-red text-sm"> {formik.errors.amountBeingPaid} </div>
              )}
            </div>
            <div className="text-G300">
              <label className="block text-sm font-normal mb-2 text-start">
                Balance Due <span className="text-red ">*</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-green font-semibold">₹</span>
                <input
                  type="text"
                  name="balanceDue"
                  className="inputtext text-green placeholder:text-green"
                  placeholder="0"
                  style={{ paddingLeft: "25px" }}
                  value={Math.trunc(formik.values.balanceDue)}
                  disabled
                />
              </div>

            </div>
          </div>
          {/* Buttons */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => {
                formik.resetForm();
                onClose();
              }}
              className="w-[75px] sm:w-[93px] py-2 text-center text-sm bg-white text-green rounded-lg border border-green"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                  setShowConfirmPopup(true);  // No errors — open confirmation popup
                } else {
                  formik.setTouched(
                    Object.keys(formik.initialValues).reduce((acc, key) => {
                      acc[key] = true;
                      return acc;
                    }, {})
                  );
                }
              }}
              className={`w-[75px] sm:w-[93px] text-sm text-center py-2 rounded-lg ${isSubmitting ? "bg-green50 cursor-not-allowed" : "bg-green text-white"
                }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Confirming..." : "Confirm"}
            </button>
          </div>
        </form>
      </div>
      {showConfirmPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[300px] shadow-lg">
            <h2 className="text-lg font-semibold mb-4 text-center">Are you sure you want to confirm this?</h2>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  setShowConfirmPopup(false);
                  formik.handleSubmit();
                }}
                className="w-[75px] py-2 text-sm text-white bg-green rounded-lg"
              >
                Yes
              </button>
              <button
                onClick={() => setShowConfirmPopup(false)}
                className="w-[75px] py-2 text-sm bg-white text-green border border-green rounded-lg"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
      {/* restart popup */}
      {showResetPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-60">
          <div className="bg-white rounded-lg p-6 w-90 text-center space-y-4">
            <h2 className="text-lg font-semibold text-G500 mb-2">Payment Successful!</h2>
            <p className="text-sm text-G300">Do you want to Restart Booking or Close ?</p>
            <div className="flex justify-center gap-3 mt-4">
              <button
                className="bg-green text-white px-4 py-2 rounded"
                onClick={() => {
                  restartBooking();
                  setShowResetPopup(false);
                }}
              >
                Restart
              </button>
              <button
                className="bg-gray-200 text-G500 px-4 py-2 rounded"
                onClick={() => {
                  formik.resetForm();
                  setShowResetPopup(false);
                  onClose();
                  fetchPlayers();
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayNow;